/*
  Yarnly Crochet — header, announcement bar, menu drawer and search modal.
  Elements: yarnly-header, yarnly-announcement-bar, yarnly-menu-drawer, yarnly-search-modal.
  Also hides expired offer end-date lines ([data-offer-end="YYYY-MM-DD"]) on cached pages.
*/
(function () {
  'use strict';
  const S = window.Yarnly;
  if (!S) return;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesktop = () => desktopQuery.matches;
  const canHover = () => hoverQuery.matches;
  const reducedMotion = () => motionQuery.matches;
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const HOVER_OPEN_DELAY = 80;
  const HOVER_CLOSE_DELAY = 220;
  const SCROLLED_OFFSET = 24;
  const TRANSPARENT_OFFSET = 40;

  /* ------------------------------------------------------------------ */
  /* yarnly-header                                                     */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('yarnly-header')) {
    class YarnlyHeader extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onEditorChange = this.onEditorChange.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
        this.measure = this.measure.bind(this);
        this.lastScrollY = 0;
        this.ticking = false;
        this.menuCleanups = [];
      }

      connectedCallback() {
        this.header = this.querySelector('.header');
        this.section = this.closest('.shopify-section') || this;
        this.sticky = this.dataset.sticky || 'none';
        this.shrink = this.hasAttribute('data-shrink');
        this.transparentAllowed = false;
        this.transparentLight = false;

        this.section.classList.toggle('header-wrapper--sticky', this.sticky !== 'none');
        this.section.classList.add('header-wrapper--flow');

        this.evaluateTransparent();
        this.setupObservers();
        this.setupMenus();

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.measure, { passive: true });
        document.addEventListener('click', this.onDocumentClick);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('keydown', this.onKeydown);

        if (S.pubsub && S.pubsub.subscribe) {
          this.unsubscribeDrawer = S.pubsub.subscribe('drawer:open', () => this.closeAllMenus());
        }

        if (inDesignMode()) {
          document.addEventListener('shopify:section:load', this.onEditorChange);
          document.addEventListener('shopify:section:unload', this.onEditorChange);
          document.addEventListener('shopify:section:reorder', this.onEditorChange);
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        this.lastScrollY = window.scrollY;
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.measure);
        document.removeEventListener('click', this.onDocumentClick);
        this.removeEventListener('focusout', this.onFocusOut);
        this.removeEventListener('keydown', this.onKeydown);
        if (this.unsubscribeDrawer) this.unsubscribeDrawer();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.menuCleanups.forEach((fn) => fn());
        this.menuCleanups = [];
        document.removeEventListener('shopify:section:load', this.onEditorChange);
        document.removeEventListener('shopify:section:unload', this.onEditorChange);
        document.removeEventListener('shopify:section:reorder', this.onEditorChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      /* Heights ------------------------------------------------------- */

      setupObservers() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.measure);
          this.resizeObserver.observe(this);
          const bar = document.querySelector('[data-announcement-bar]');
          if (bar) this.resizeObserver.observe(bar);
        }
        this.measure();
      }

      measure() {
        const root = document.documentElement;
        const height = this.offsetHeight;
        root.style.setProperty('--header-height', height + 'px');
        if (!this.classList.contains('header--scrolled')) {
          this.section.style.setProperty('--header-flow-height', height + 'px');
        }
        const bar = document.querySelector('[data-announcement-bar]');
        root.style.setProperty('--announcement-height', (bar ? bar.offsetHeight : 0) + 'px');
      }

      /* Scroll state --------------------------------------------------- */

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(this.update);
      }

      update() {
        this.ticking = false;
        const y = Math.max(window.scrollY, 0);
        const scrolled = y > SCROLLED_OFFSET;
        this.classList.toggle('header--scrolled', scrolled);

        if (this.sticky === 'on-scroll-up') {
          const goingDown = y > this.lastScrollY;
          const pastHeader = y > this.offsetHeight + SCROLLED_OFFSET;
          const menuOpen = this.classList.contains('header--menu-open');
          const hide = goingDown && pastHeader && !menuOpen;
          if (hide !== this.section.classList.contains('header--hidden')) {
            this.section.classList.toggle('header--hidden', hide);
            this.classList.toggle('header--hidden', hide);
            if (hide) this.closeAllMenus();
          }
        }
        this.lastScrollY = y;

        this.applyTransparent(y);
      }

      /* Transparent header -------------------------------------------- */

      evaluateTransparent() {
        this.transparentAllowed = false;
        this.transparentLight = false;
        if (!this.hasAttribute('data-transparent-candidate')) return;
        if (!document.body.classList.contains('allow-transparent-header')) return;
        const main = document.getElementById('MainContent');
        const firstSection = main ? main.querySelector('.shopify-section') : null;
        const hero = firstSection ? firstSection.querySelector('[data-allow-transparent-header]') : null;
        if (!hero) return;
        this.transparentAllowed = true;
        this.transparentLight = hero.getAttribute('data-transparent-text') === 'light';
      }

      applyTransparent(y) {
        const scrollY = typeof y === 'number' ? y : window.scrollY;
        const overlay = this.transparentAllowed;
        this.section.classList.toggle('header-wrapper--overlay', overlay);
        const transparent = overlay && scrollY <= TRANSPARENT_OFFSET && !this.classList.contains('header--menu-open');
        this.classList.toggle('header--transparent', transparent);
        this.classList.toggle('header--transparent-light', transparent && this.transparentLight);
      }

      onEditorChange() {
        window.requestAnimationFrame(() => {
          this.evaluateTransparent();
          this.setupObservers();
          this.update();
        });
      }

      /* Desktop submenus ---------------------------------------------- */

      setupMenus() {
        this.menuCleanups.forEach((fn) => fn());
        this.menuCleanups = [];
        this.detailsList = Array.from(this.querySelectorAll('[data-menu-details]'));

        this.detailsList.forEach((details) => {
          const summary = details.querySelector('summary');
          const item = details.closest('li') || details;
          let openTimer = null;
          let closeTimer = null;
          let hoverOpened = false;

          const onToggle = () => {
            if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
            if (details.open) {
              this.closeOtherMenus(details);
              this.classList.add('header--menu-open');
            } else if (!this.anyMenuOpen()) {
              this.classList.remove('header--menu-open');
            }
            this.applyTransparent();
          };

          const onPointerEnter = (event) => {
            if (event.pointerType !== 'mouse' || !isDesktop() || !canHover()) return;
            window.clearTimeout(closeTimer);
            if (details.open) return;
            openTimer = window.setTimeout(() => {
              hoverOpened = true;
              details.open = true;
            }, HOVER_OPEN_DELAY);
          };

          const onPointerLeave = (event) => {
            if (event.pointerType !== 'mouse') return;
            window.clearTimeout(openTimer);
            closeTimer = window.setTimeout(() => {
              hoverOpened = false;
              if (details.open && !details.contains(document.activeElement)) details.open = false;
            }, HOVER_CLOSE_DELAY);
          };

          const onSummaryClick = (event) => {
            if (hoverOpened && details.open) {
              /* A click right after a hover-open keeps the panel open instead of toggling it shut. */
              event.preventDefault();
              hoverOpened = false;
            }
          };

          details.addEventListener('toggle', onToggle);
          item.addEventListener('pointerenter', onPointerEnter);
          item.addEventListener('pointerleave', onPointerLeave);
          if (summary) summary.addEventListener('click', onSummaryClick);

          this.menuCleanups.push(() => {
            window.clearTimeout(openTimer);
            window.clearTimeout(closeTimer);
            details.removeEventListener('toggle', onToggle);
            item.removeEventListener('pointerenter', onPointerEnter);
            item.removeEventListener('pointerleave', onPointerLeave);
            if (summary) summary.removeEventListener('click', onSummaryClick);
          });
        });
      }

      anyMenuOpen() {
        return (this.detailsList || []).some((details) => details.open);
      }

      closeOtherMenus(except) {
        (this.detailsList || []).forEach((details) => {
          if (details !== except && details.open) details.open = false;
        });
      }

      closeAllMenus(returnFocus) {
        (this.detailsList || []).forEach((details) => {
          if (!details.open) return;
          details.open = false;
          if (returnFocus) {
            const summary = details.querySelector('summary');
            if (summary) summary.focus();
          }
        });
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        const details = event.target.closest('[data-menu-details]');
        if (details && details.open) {
          event.preventDefault();
          event.stopPropagation();
          details.open = false;
          const summary = details.querySelector('summary');
          if (summary) summary.focus();
        }
      }

      onDocumentClick(event) {
        if (!this.anyMenuOpen()) return;
        if (this.contains(event.target)) return;
        this.closeAllMenus();
      }

      onFocusOut(event) {
        const next = event.relatedTarget;
        if (!next || this.contains(next)) return;
        this.closeAllMenus();
      }

      /* Theme editor -------------------------------------------------- */

      onBlockSelect(event) {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const panel = this.querySelector('[data-block-id="' + blockId + '"]');
        if (!panel) return;
        const details = panel.closest('[data-menu-details]');
        if (details) details.open = true;
      }

      onBlockDeselect(event) {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const panel = this.querySelector('[data-block-id="' + blockId + '"]');
        if (!panel) return;
        const details = panel.closest('[data-menu-details]');
        if (details) details.open = false;
      }
    }
    customElements.define('yarnly-header', YarnlyHeader);
  }

  /* ------------------------------------------------------------------ */
  /* Offer end-date lines                                               */
  /* ------------------------------------------------------------------ */
  /*
    <p data-offer-end="YYYY-MM-DD"> is rendered by Liquid only while the date is
    today or later (theme setting "Offer end date"). A cached page can outlive
    that day, so the line hides itself once the visitor's local date is past it.
    The end date is inclusive. Nothing ticks: the check runs on load, when the
    announcement bar connects and after a section reload in the editor. A host
    marked [data-offer-only] (a bar whose only content is the line) hides too.
    Malformed dates are left alone.
  */
  const OFFER_DATE = /^\d{4}-\d{2}-\d{2}$/;

  function localIsoDay(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function hideExpiredOffers(scope) {
    const root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    const nodes = Array.from(root.querySelectorAll('[data-offer-end]'));
    if (typeof root.matches === 'function' && root.matches('[data-offer-end]')) nodes.push(root);
    if (!nodes.length) return;
    const today = localIsoDay(new Date());
    nodes.forEach((el) => {
      const end = String(el.getAttribute('data-offer-end') || '').trim();
      if (!OFFER_DATE.test(end) || end >= today) return;
      el.hidden = true;
      const host = el.closest('[data-offer-only]');
      if (host) host.hidden = true;
    });
  }

  if (!S._offerEndBound) {
    S._offerEndBound = true;
    const sweep = () => hideExpiredOffers(document);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sweep);
    } else {
      sweep();
    }
    document.addEventListener('shopify:section:load', (event) => hideExpiredOffers(event.target));
  }

  /* ------------------------------------------------------------------ */
  /* yarnly-announcement-bar                                           */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('yarnly-announcement-bar')) {
    class YarnlyAnnouncementBar extends HTMLElement {
      constructor() {
        super();
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.onVisibility = this.onVisibility.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
        this.tick = this.tick.bind(this);
        this.timer = null;
        this.userPaused = false;
        this.hovering = false;
        this.editorHold = false;
      }

      connectedCallback() {
        hideExpiredOffers(this);
        this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
        this.track = this.querySelector('[data-announcement-slides]');
        if (this.slides.length < 2) return;

        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.prevButton = this.querySelector('[data-announcement-prev]');
        this.nextButton = this.querySelector('[data-announcement-next]');
        this.toggleButton = this.querySelector('[data-announcement-toggle]');
        this.toggleLabel = this.querySelector('[data-announcement-toggle-label]');
        this.autoplay = parseInt(this.dataset.autoplay, 10) || 0;
        if (reducedMotion()) this.autoplay = 0;

        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
        if (this.toggleButton) {
          if (this.autoplay > 0) {
            this.toggleButton.addEventListener('click', this.onToggle);
          } else {
            this.toggleButton.hidden = true;
          }
        }

        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
        document.addEventListener('visibilitychange', this.onVisibility);

        if (inDesignMode()) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        this.show(this.index, false);
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        if (this.toggleButton) this.toggleButton.removeEventListener('click', this.onToggle);
        this.removeEventListener('mouseenter', this.onEnter);
        this.removeEventListener('mouseleave', this.onLeave);
        this.removeEventListener('focusin', this.onEnter);
        this.removeEventListener('focusout', this.onLeave);
        document.removeEventListener('visibilitychange', this.onVisibility);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      show(index, announce) {
        const total = this.slides.length;
        this.index = ((index % total) + total) % total;
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.classList.toggle('is-active', active);
          if (active) {
            slide.removeAttribute('aria-hidden');
          } else {
            slide.setAttribute('aria-hidden', 'true');
          }
          slide.querySelectorAll('a').forEach((link) => {
            if (active) {
              link.removeAttribute('tabindex');
            } else {
              link.setAttribute('tabindex', '-1');
            }
          });
        });
        if (this.track) this.track.setAttribute('aria-live', announce ? 'polite' : 'off');
      }

      onPrev() {
        this.show(this.index - 1, true);
        this.restart();
      }

      onNext() {
        this.show(this.index + 1, true);
        this.restart();
      }

      onToggle() {
        this.userPaused = !this.userPaused;
        if (this.toggleButton) {
          this.toggleButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
          const label = this.userPaused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
          if (this.toggleLabel && label) this.toggleLabel.textContent = label;
        }
        if (this.userPaused) {
          this.stop();
        } else {
          this.start();
        }
      }

      onEnter() {
        this.hovering = true;
        this.stop();
      }

      onLeave(event) {
        if (event && event.type === 'focusout' && this.contains(event.relatedTarget)) return;
        if (event && event.type === 'mouseleave' && this.contains(document.activeElement) && document.activeElement !== document.body) return;
        this.hovering = false;
        this.start();
      }

      onVisibility() {
        if (document.hidden) {
          this.stop();
        } else {
          this.start();
        }
      }

      canPlay() {
        return this.autoplay > 0 && !this.userPaused && !this.hovering && !this.editorHold && !document.hidden;
      }

      start() {
        this.stop();
        if (!this.canPlay()) return;
        this.timer = window.setInterval(this.tick, this.autoplay);
      }

      stop() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      restart() {
        this.stop();
        this.start();
      }

      tick() {
        this.show(this.index + 1, false);
      }

      onBlockSelect(event) {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const target = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
        if (target < 0) return;
        this.editorHold = true;
        this.stop();
        this.show(target, false);
      }

      onBlockDeselect(event) {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId || !this.slides.some((slide) => slide.dataset.blockId === blockId)) return;
        this.editorHold = false;
        this.start();
      }
    }
    customElements.define('yarnly-announcement-bar', YarnlyAnnouncementBar);
  }

  /* ------------------------------------------------------------------ */
  /* yarnly-menu-drawer                                                */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('yarnly-menu-drawer')) {
    class YarnlyMenuDrawer extends HTMLElement {
      constructor() {
        super();
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
      }

      connectedCallback() {
        this.drawer = this.querySelector('yarnly-drawer');
        if (!this.drawer) return;
        this.drawerId = this.drawer.id;
        this.drawer.addEventListener('drawer:open', this.onOpen);
        this.drawer.addEventListener('drawer:close', this.onClose);
        if (S.pubsub && S.pubsub.subscribe) {
          this.unsubscribeOpen = S.pubsub.subscribe('drawer:open', (payload) => {
            if (payload && payload.id === this.drawerId) this.onOpen();
          });
          this.unsubscribeClose = S.pubsub.subscribe('drawer:close', (payload) => {
            if (payload && payload.id === this.drawerId) this.onClose();
          });
        }
      }

      disconnectedCallback() {
        if (this.drawer) {
          this.drawer.removeEventListener('drawer:open', this.onOpen);
          this.drawer.removeEventListener('drawer:close', this.onClose);
        }
        if (this.unsubscribeOpen) this.unsubscribeOpen();
        if (this.unsubscribeClose) this.unsubscribeClose();
      }

      onOpen() {
        window.requestAnimationFrame(() => {
          const first = this.querySelector('.menu-drawer__list a, .menu-drawer__list summary');
          if (first && this.drawer.classList.contains('is-open')) first.focus({ preventScroll: true });
        });
      }

      onClose() {
        this.querySelectorAll('[data-menu-drawer-details][open]').forEach((details) => {
          details.open = false;
        });
      }
    }
    customElements.define('yarnly-menu-drawer', YarnlyMenuDrawer);
  }

  /* ------------------------------------------------------------------ */
  /* yarnly-search-modal                                               */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('yarnly-search-modal')) {
    class YarnlySearchModal extends HTMLElement {
      constructor() {
        super();
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
      }

      connectedCallback() {
        this.drawer = this.querySelector('yarnly-drawer');
        if (!this.drawer) return;
        this.drawerId = this.drawer.id;
        this.drawer.addEventListener('drawer:open', this.onOpen);
        this.drawer.addEventListener('drawer:close', this.onClose);
        if (S.pubsub && S.pubsub.subscribe) {
          this.unsubscribeOpen = S.pubsub.subscribe('drawer:open', (payload) => {
            if (payload && payload.id === this.drawerId) this.onOpen();
          });
          this.unsubscribeClose = S.pubsub.subscribe('drawer:close', (payload) => {
            if (payload && payload.id === this.drawerId) this.onClose();
          });
        }
      }

      disconnectedCallback() {
        if (this.drawer) {
          this.drawer.removeEventListener('drawer:open', this.onOpen);
          this.drawer.removeEventListener('drawer:close', this.onClose);
        }
        if (this.unsubscribeOpen) this.unsubscribeOpen();
        if (this.unsubscribeClose) this.unsubscribeClose();
      }

      onOpen() {
        window.requestAnimationFrame(() => {
          const input = this.querySelector('[data-predictive-input]');
          if (input && this.drawer.classList.contains('is-open')) {
            input.focus({ preventScroll: true });
            input.select();
          }
        });
      }

      onClose() {
        const search = this.querySelector('yarnly-predictive-search');
        if (search && typeof search.clear === 'function') {
          search.clear();
          return;
        }
        const input = this.querySelector('[data-predictive-input]');
        if (input) input.value = '';
        const results = this.querySelector('[data-predictive-results]');
        if (results) {
          results.hidden = true;
          results.innerHTML = '';
        }
      }
    }
    customElements.define('yarnly-search-modal', YarnlySearchModal);
  }
})();
